// Anchor tab nav — scroll-spy + stuck state for the product-anchors section.
// Progressive enhancement: without JS the row is plain in-page anchor links and
// CSS position:sticky still pins it (at top: 0).
(() => {
  const nav = document.querySelector('[data-anchor-nav]');
  if (!nav || !('IntersectionObserver' in window)) return;

  const pairs = [...nav.querySelectorAll('.product-anchors__tab')]
    .map((link) => [link, document.getElementById(decodeURIComponent(link.hash.slice(1)))])
    .filter(([, target]) => target);
  if (!pairs.length) return;
  const targets = pairs.map(([, target]) => target);
  const row = nav.querySelector('.product-anchors__row');

  // Overflow edges: the CSS fades a side only while tabs are hidden past it.
  const edges = () => {
    const max = row.scrollWidth - row.clientWidth;
    row.toggleAttribute('data-more-start', max > 1 && row.scrollLeft > 1);
    row.toggleAttribute('data-more-end', max > 1 && row.scrollLeft < max - 1);
  };
  row.addEventListener('scroll', edges, { passive: true });
  new ResizeObserver(edges).observe(row);
  edges();

  // Keep the active tab in view inside a row that overflows: centre it, which also
  // brings the next tab on screen. The row's own scrollLeft, never scrollIntoView,
  // which would also move the page mid scroll-spy.
  const reveal = (link) => {
    const max = row.scrollWidth - row.clientWidth;
    if (!link || max <= 1) return;
    const r = row.getBoundingClientRect(), l = link.getBoundingClientRect();
    const left = row.scrollLeft + (l.left - r.left) - (r.width - l.width) / 2;
    row.scrollTo({ left: Math.max(0, Math.min(max, left)), behavior: 'smooth' });
  };
  const setActive = (active) => {
    let changed = null;
    pairs.forEach(([link]) => {
      const on = link === active;
      if (on && !link.classList.contains('is-active')) changed = link;
      link.classList.toggle('is-active', on);
    });
    if (changed) reveal(changed);
  };

  // Offset anchors + spy line past the sticky header and the row itself.
  // ponytail: hide-on-scroll header leaves a small gap while hidden; measure on
  // scroll if it ever matters.
  const header = document.querySelector('.header--sticky');
  const headerH = header ? Math.round(header.getBoundingClientRect().height) : 0;
  const wrap = nav.closest('.shopify-section') || nav;
  wrap.style.setProperty('--anchor-top', `${headerH}px`);
  const offset = headerH + nav.offsetHeight + 8;
  targets.forEach((t) => (t.style.scrollMarginTop = `${offset}px`));

  // Clicking a tab activates it immediately — the spy would otherwise mis-pick
  // the previous section, whose bottom edge lands touching the spy line (IO
  // counts edge-touch as intersecting). The lock keeps the spy from hopping
  // through intermediate sections during the smooth scroll; scrollend releases
  // it, the timeout is the fallback (no scrollend / already at target).
  let lock = null;
  pairs.forEach(([link]) => {
    link.addEventListener('click', () => {
      setActive(link);
      lock = link;
      setTimeout(() => { if (lock === link) lock = null; }, 1500);
    });
  });
  window.addEventListener('scrollend', () => { lock = null; });

  // Scroll-spy: the first visible target (DOM order) owns the underline.
  const visible = new Set();
  const spy = new IntersectionObserver(
    (entries) => {
      entries.forEach((e) => (e.isIntersecting ? visible.add(e.target) : visible.delete(e.target)));
      if (lock) {
        // Mid smooth-scroll after a click: hold the clicked tab active.
        setActive(lock);
        return;
      }
      const current = targets.find((t) => visible.has(t));
      setActive(pairs.find(([, target]) => target === current)?.[0] ?? null);
    },
    // Band top sits 4px past the anchor landing line so a section whose bottom
    // edge exactly touches it (the section above a jump target) doesn't count.
    { rootMargin: `-${offset + 4}px 0px -55% 0px` }
  );
  targets.forEach((t) => spy.observe(t));

  // Stuck elevation: a zero-height sentinel just above the row; once it scrolls
  // past the header line the row is pinned.
  if (nav.classList.contains('product-anchors--sticky')) {
    const sentinel = document.createElement('span');
    wrap.before(sentinel);
    new IntersectionObserver(
      ([e]) => nav.classList.toggle('is-stuck', !e.isIntersecting),
      { rootMargin: `-${headerH + 1}px 0px 0px 0px` }
    ).observe(sentinel);
  }
})();
